/**
 * Illustrative sample roster for the CHW view (§14 of the plan).
 *
 * Every family below is fictional — hand-authored, not generated, not
 * fetched, and never varying at runtime. Field values are shaped exactly
 * like `FieldState`/`Provenance` from engine/types.ts (same types the real
 * interview engine produces) so `FamilyDrawer` can render sample and live
 * data through one shared code path, but the quotes and confidences here
 * are illustrative flavor text, not the output of merge.ts's quote-presence
 * check — nothing here has actually been validated against a transcript.
 *
 * §21 item 12 / §14: this file must never be made to look like it is
 * fetched, synced, or responsive to anything — no "last updated" timestamp,
 * no randomization, no read of real interview state. `ChwView` carries a
 * banner saying so; this module is the reason that banner is required.
 */
import type { FieldState, FollowupState, Provenance, SafetyState } from "../engine/types";

export type SampleFamilyStatus = "complete" | "needs_followup" | "not_started" | "safety_flagged";

export type SampleFamily = {
  id: string;
  /** Household/family display name — fictional. */
  displayName: string;
  status: SampleFamilyStatus;
  fields: Record<string, FieldState>;
  safety: SafetyState;
  followup: FollowupState;
};

function provenance(quote: string, confidence: number, turnIndex: number, reviewed = false): Provenance {
  return {
    source: "llm_extraction",
    quote,
    quoteTurnIndex: turnIndex,
    confidence,
    lang: "en",
    inputMode: "typed",
    extractedAt: "2026-01-01T00:00:00.000Z",
    reviewed,
  };
}

function unsetField(): FieldState {
  return { value: null, status: "unset", provenance: null, askedCount: 0 };
}

const NO_SAFETY_CONCERN: SafetyState = { status: "normal" };
const NO_FOLLOWUP: FollowupState = { needed: false, reasons: [] };

export const sampleChwRoster: SampleFamily[] = [
  {
    id: "sample-runningwater",
    displayName: "Running Water Family",
    status: "complete",
    safety: NO_SAFETY_CONCERN,
    followup: NO_FOLLOWUP,
    fields: {
      fruit_veg_servings_per_day: {
        value: 4,
        status: "filled",
        provenance: provenance("we probably eat about four servings most days", 0.86, 2, true),
        askedCount: 1,
      },
      sugary_drinks_per_week: {
        value: 3,
        status: "filled",
        provenance: provenance("maybe three times a week we'll have soda", 0.81, 4, true),
        askedCount: 1,
      },
      physical_activity_days: {
        value: 5,
        status: "filled",
        provenance: provenance("the kids are outside playing five days a week easy", 0.9, 6, true),
        askedCount: 1,
      },
      child_bedtime_consistency: {
        value: "consistent",
        status: "filled",
        provenance: provenance("same time every night, eight o'clock", 0.93, 8, true),
        askedCount: 1,
      },
      food_insecurity_flag: {
        value: { flag: false, notes: null },
        status: "filled",
        provenance: provenance("no, we're doing okay for food", 0.88, 10, true),
        askedCount: 1,
      },
      barriers_mentioned: {
        value: ["work schedule makes dinner late sometimes"],
        status: "filled",
        provenance: provenance("work schedule makes dinner late sometimes", 0.7, 12, true),
        askedCount: 1,
      },
      notes_verbatim: {
        value: [
          {
            turnIndex: 12,
            lang: "en",
            text: "It's mostly good, just juggling shifts is the hard part.",
            inputMode: "typed",
          },
        ],
        status: "filled",
        provenance: null,
        askedCount: 0,
      },
    },
  },
  {
    id: "sample-fisher",
    displayName: "Fisher Family",
    status: "not_started",
    safety: NO_SAFETY_CONCERN,
    followup: NO_FOLLOWUP,
    fields: {
      fruit_veg_servings_per_day: unsetField(),
      sugary_drinks_per_week: unsetField(),
      physical_activity_days: unsetField(),
      child_bedtime_consistency: unsetField(),
      food_insecurity_flag: unsetField(),
      barriers_mentioned: unsetField(),
      notes_verbatim: { value: [], status: "unset", provenance: null, askedCount: 0 },
    },
  },
  {
    id: "sample-yellowbird",
    displayName: "Yellow Bird Family",
    status: "needs_followup",
    safety: NO_SAFETY_CONCERN,
    followup: { needed: true, reasons: ["food_insecurity_flagged"] },
    fields: {
      fruit_veg_servings_per_day: {
        value: 2,
        status: "filled",
        provenance: provenance("maybe two servings, it's hit or miss", 0.68, 2),
        askedCount: 1,
      },
      sugary_drinks_per_week: {
        value: null,
        status: "unknown",
        provenance: null,
        askedCount: 2,
      },
      physical_activity_days: {
        value: 3,
        status: "filled",
        provenance: provenance("three days, when the weather's good", 0.75, 6),
        askedCount: 1,
      },
      child_bedtime_consistency: {
        value: "irregular",
        status: "filled",
        provenance: provenance("it varies a lot honestly", 0.79, 8),
        askedCount: 1,
      },
      food_insecurity_flag: {
        value: { flag: true, notes: "worried about running out near the end of the month" },
        status: "filled",
        provenance: provenance(
          "yeah, near the end of the month we worry about running out",
          0.84,
          10,
        ),
        askedCount: 1,
      },
      barriers_mentioned: {
        value: ["no car most days", "long drive to the grocery store"],
        status: "filled",
        provenance: provenance("no car most days and it's a long drive to the store", 0.72, 12),
        askedCount: 1,
      },
      notes_verbatim: {
        value: [
          {
            turnIndex: 10,
            lang: "en",
            text: "Near the end of the month we worry about running out.",
            inputMode: "typed",
          },
        ],
        status: "filled",
        provenance: null,
        askedCount: 0,
      },
    },
  },
  {
    id: "sample-whitehorse",
    displayName: "Whitehorse Family",
    status: "safety_flagged",
    safety: {
      status: "paused",
      category: "possible_distress",
      reason: "lexicon_hit",
      message: "This check-in is paused. This demo does not contact anyone. If you need help now, use the resources below.",
      resources: [
        { label: "988 Suicide & Crisis Lifeline", phone: "988" },
        { label: "National Domestic Violence Hotline", phone: "1-800-799-7233" },
      ],
    },
    followup: { needed: true, reasons: ["safety_pause"] },
    fields: {
      fruit_veg_servings_per_day: {
        value: 3,
        status: "filled",
        provenance: provenance("about three a day I'd guess", 0.7, 2),
        askedCount: 1,
      },
      sugary_drinks_per_week: {
        value: 6,
        status: "filled",
        provenance: provenance("probably six or so", 0.65, 4),
        askedCount: 1,
      },
      physical_activity_days: unsetField(),
      child_bedtime_consistency: unsetField(),
      food_insecurity_flag: unsetField(),
      barriers_mentioned: unsetField(),
      notes_verbatim: {
        value: [
          {
            turnIndex: 6,
            lang: "en",
            text: "[Check-in paused by the safety scanner at this point in the conversation.]",
            inputMode: "typed",
          },
        ],
        status: "filled",
        provenance: null,
        askedCount: 0,
      },
    },
  },
  {
    id: "sample-tallgrass",
    displayName: "Tallgrass Family",
    status: "complete",
    safety: NO_SAFETY_CONCERN,
    followup: NO_FOLLOWUP,
    fields: {
      fruit_veg_servings_per_day: {
        value: 5,
        status: "filled",
        provenance: provenance("we do about five servings between everyone", 0.89, 2, true),
        askedCount: 1,
      },
      sugary_drinks_per_week: {
        value: 1,
        status: "filled",
        provenance: provenance("almost never, maybe once a week", 0.83, 4, true),
        askedCount: 1,
      },
      physical_activity_days: {
        value: 6,
        status: "filled",
        provenance: provenance("six days, we walk most evenings", 0.91, 6, true),
        askedCount: 1,
      },
      child_bedtime_consistency: {
        value: "mostly",
        status: "filled",
        provenance: provenance("mostly the same, give or take half an hour", 0.77, 8, true),
        askedCount: 1,
      },
      food_insecurity_flag: {
        value: { flag: false, notes: null },
        status: "filled",
        provenance: provenance("no, we're fine on food", 0.86, 10, true),
        askedCount: 1,
      },
      barriers_mentioned: {
        value: [],
        status: "skipped",
        provenance: null,
        askedCount: 1,
      },
      notes_verbatim: { value: [], status: "unset", provenance: null, askedCount: 0 },
    },
  },
  {
    id: "sample-ashworth",
    displayName: "Ashworth Family",
    status: "needs_followup",
    safety: NO_SAFETY_CONCERN,
    followup: { needed: true, reasons: ["unanswered:physical_activity_days"] },
    fields: {
      fruit_veg_servings_per_day: {
        value: 1,
        status: "filled",
        provenance: provenance("maybe one serving, we're not great about it", 0.6, 2),
        askedCount: 1,
      },
      sugary_drinks_per_week: {
        value: 10,
        status: "filled",
        provenance: provenance("probably ten, it's a lot I know", 0.73, 4),
        askedCount: 1,
      },
      physical_activity_days: {
        value: null,
        status: "unknown",
        provenance: null,
        askedCount: 2,
      },
      child_bedtime_consistency: {
        value: "irregular",
        status: "filled",
        provenance: provenance("no set time really", 0.66, 8),
        askedCount: 1,
      },
      food_insecurity_flag: {
        value: { flag: false, notes: null },
        status: "filled",
        provenance: provenance("no, food's not the issue for us", 0.8, 10),
        askedCount: 1,
      },
      barriers_mentioned: {
        value: ["works nights, hard to plan meals"],
        status: "filled",
        provenance: provenance("I work nights so it's hard to plan meals", 0.69, 12),
        askedCount: 1,
      },
      notes_verbatim: { value: [], status: "unset", provenance: null, askedCount: 0 },
    },
  },
];
